import { Badge, type AttachmentItem } from '@evogard/ui';
import {
  canAccess,
  daysUntil,
  isOverdue,
  formatDate,
  refusalToActOnTicket,
  ticketStatusLabel,
  ticketStatusTone,
  type TicketAct,
  type TicketStatus,
  type TicketSubject,
  type TicketViewer,
} from '@evogard/domain';
import type { Attachment, TicketSummary } from '@evogard/contract';
import { useQuery } from '@tanstack/react-query';
import { api, ok } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';

/**
 * Quem a pessoa é nas demandas desta base, segundo o servidor
 * (`openingProfile`): `true` é consultor, que só abre e acompanha — decisão do
 * dono em 23/09/2026. `undefined` enquanto não se sabe.
 *
 * Uma chave só, com a base dentro, para o formulário de abertura, a mesa e o
 * detalhe: o vínculo de consultor é da praça, e trocar de base não pode servir
 * a resposta da outra. Quem não pode abrir demanda não pergunta — a rota pede
 * `criar` — e é colaborador para a tela; quem decide continua o servidor.
 */
export function useIsConsultant(): boolean | undefined {
  const { data: context } = useSessionContext();
  const canCreate = can(context, 'tickets.tickets', 'criar');
  const profile = useQuery({
    queryKey: ['demandas', 'abertura', 'perfil', context?.currentTenant.id],
    enabled: canCreate,
    queryFn: async () => {
      const r = await api.tickets.openingProfile();
      return ok(r, 200, 'Não foi possível saber seu perfil de abertura.');
    },
  });
  if (context && !canCreate) return false;
  return profile.data ? profile.data.profile === 'consultor' : undefined;
}

/*
 * Os formatadores moravam aqui e subiram para `@evogard/domain`: a tela de
 * associados e a de "minha conta" precisaram das mesmas, e o lint derrubou o
 * import do vizinho — com razão. O que dois módulos precisam não é de nenhum
 * dos dois, e o ganho maior é as duas SPAs escreverem data do mesmo jeito.
 */
export { count, formatDate, formatInstant } from '@evogard/domain';

/**
 * O vocabulário visual de demandas, num lugar só.
 *
 * Situação e prazo aparecem na lista, no detalhe, no painel do início e nas
 * duas telas do portal. Repetir o `switch` de cor em cada uma é como a mesma
 * situação vira verde numa tela e cinza na outra — e é o rótulo, escrito duas
 * vezes, que diverge primeiro.
 *
 * O texto vem de `@evogard/domain`, não daqui: é o mesmo que vai no e-mail.
 */

export function TicketStatusBadge({ situacao }: { situacao: TicketStatus }) {
  return <Badge tone={ticketStatusTone(situacao)}>{ticketStatusLabel(situacao)}</Badge>;
}

/**
 * O prazo, dito como gente fala.
 *
 * "Sem prazo" é uma resposta legítima e aparece assim: a categoria pode não ter
 * prazo definido, e a parte 7 decidiu que o que não se sabe fica de fora
 * inteiro em vez de virar um padrão plausível. A tela diz o que é.
 */
export function Deadline({
  dueAt,
  completed = false,
}: {
  dueAt: string | null;
  completed?: boolean;
}) {
  if (!dueAt) {
    return <span className="text-texto-suave">Sem prazo</span>;
  }

  const data = formatDate(dueAt);
  // demanda concluída não vence: o prazo virou histórico, não cobrança
  if (completed) return <span className="text-texto-suave">{data}</span>;

  if (isOverdue(dueAt)) {
    const days = Math.abs(daysUntil(dueAt));
    return (
      <span className="font-medium text-erro">
        {data} · {days === 1 ? 'venceu ontem' : `venceu há ${days} dias`}
      </span>
    );
  }

  const missing = daysUntil(dueAt);
  return (
    <span className={missing <= 1 ? 'font-medium text-alerta' : undefined}>
      {data}
      {missing === 0 && ' · vence hoje'}
      {missing === 1 && ' · vence amanhã'}
    </span>
  );
}

/**
 * O anexo da demanda, no formato que a grade de `@evogard/ui` desenha.
 *
 * ## O que saiu daqui, e por quê
 *
 * Era a `DocumentRow`: uma linha com ícone de documento, nome, tamanho, autor e
 * um botão "Baixar" que virava um link "Abrir" depois da resposta. Ela
 * funcionava e não mostrava **o arquivo** — três capturas de tela anexadas na
 * mesma demanda eram três linhas iguais, e descobrir qual é qual custava três
 * downloads.
 *
 * A grade (`AttachmentGallery`) desenha a prévia, e o que ela precisa de cada
 * anexo é só isto: um nome, um tipo, um tamanho e **como chegar ao conteúdo**.
 *
 * ## Por que `resolveUrl` é uma função, e não uma URL
 *
 * A URL do anexo é assinada e vale cinco minutos. Assiná-la ao montar a tela
 * seria uma requisição por anexo para desenhar miniaturas que ninguém pediu, e
 * a assinatura teria vencido quando alguém finalmente clicasse. Em função, ela
 * é pedida uma vez por ladrilho e só para o que dá para pré-visualizar.
 *
 * O motivo de o link aparecer só DEPOIS da resposta continua o mesmo e é da
 * `AttachmentGallery` agora: `window.open` num retorno assíncrono é bloqueado
 * pelo navegador — o clique que o autorizaria já terminou. Por isso o
 * visualizador abre no lugar, com o `<a href>` já montado lá dentro.
 */
export function attachmentItem(
  attachment: Attachment,
  ticketId: number,
  detail?: string,
): AttachmentItem {
  return {
    key: String(attachment.id),
    nome: attachment.nome,
    typeMime: attachment.typeMime,
    sizeBytes: attachment.sizeBytes,
    detail,
    resolveUrl: async () => {
      const r = await api.tickets.downloadAttachment({
        params: { id: ticketId, attachmentId: attachment.id },
      });
      return ok(r, 200, 'Não foi possível abrir o documento.').url;
    },
  };
}

/* ------------------------------------------------------------------------- *
 * O papel na demanda, do lado da tela
 * ------------------------------------------------------------------------- */

/**
 * Quem está olhando, montado a partir do contexto da sessão.
 *
 * Os setores vêm de `GET /platform/context` — ver o docblock de `departments`
 * lá. Quem DECIDE continua sendo o servidor, a cada ato; isto existe para a
 * tela antecipar a mesma frase, e a frase é literalmente a mesma porque as
 * duas pontas chamam `refusalToActOnTicket` de `@evogard/domain`.
 */
export function viewerFrom(
  context:
    | {
        permissions: string[];
        departments?: ReadonlyArray<{ id: string; leads: boolean }>;
      }
    | null
    | undefined,
  /** de `useIsConsultant`; `undefined` (ainda não se sabe) vale como colaborador */
  consultant: boolean | undefined = false,
): TicketViewer | null {
  /*
   * `departments` AUSENTE devolve `null`, e não uma lista vazia.
   *
   * Os dois são diferentes: lista vazia é "não está em setor nenhum" — um fato,
   * que desabilita ações e escolhe a frase do vazio. Ausente é "ainda não sei",
   * e tratá-lo como o primeiro faria a tela AFIRMAR que a pessoa não tem setor
   * quando ela só não recebeu a resposta.
   *
   * O campo é obrigatório no contrato, então "ausente" só acontece com uma API
   * mais velha que a tela — e aconteceu: um processo da véspera segurando a
   * :3300 enquanto o `dist` já tinha o código novo (a armadilha nº 2 do
   * CLAUDE.md). O `.map` sobre `undefined` derrubava a MESA INTEIRA com "Cannot
   * read properties of undefined", que é uma tela branca para um campo que só
   * decide qual botão fica cinza.
   *
   * Com `null`, `refusalFor` devolve `false` e nada é desabilitado — quem
   * clicar recebe a recusa do servidor, que é a verdadeira. Degradar para "o
   * servidor decide" é a direção segura.
   */
  if (!context?.departments) return null;

  /*
   * O MESMO AND do servidor: a permissão diz que a pessoa pode exercer
   * liderança, o vínculo diz em quais setores. Ver `viewerOf` em
   * `tickets.service.ts` — se as duas pontas divergirem aqui, a tela oferece um
   * botão que o servidor recusa, ou esconde um que ele aceitaria.
   */
  const mayLead = canAccess(context.permissions, 'tickets.tickets', 'liderar');

  return {
    userId: '',
    departmentIds: context.departments.map((d) => d.id),
    leads: mayLead ? context.departments.filter((d) => d.leads).map((d) => d.id) : [],
    seesAny: canAccess(context.permissions, 'tickets.tickets', 'ver_qualquer'),
    consultant: consultant === true,
  };
}

/**
 * A demanda pelo que decide acesso, a partir da linha da mesa.
 *
 * `personSchema` traz `id` nulo quando quem abriu foi o associado ou o sistema
 * — e é exatamente o que o domínio espera em `requesterUserId`.
 */
export function subjectFrom(d: TicketSummary): TicketSubject {
  return {
    requesterUserId: d.requester.tipo === 'usuario' ? d.requester.id : null,
    onBehalfOfUserId: d.onBehalfOf?.id ?? null,
    assigneeId: d.assignee?.id ?? null,
    departmentId: d.department?.id ?? null,
    situacao: d.situacao,
  };
}

/**
 * O motivo de a ação não estar disponível — ou `false`, que é o que
 * `ActionsModal` espera quando ela está.
 *
 * Devolve `false` (e não uma recusa) enquanto o contexto não chegou: o painel
 * de ações é montado no primeiro render, e desabilitar tudo com "carregando"
 * seria a tela afirmando uma recusa que ela ainda não sabe se existe. Quem
 * clicar cedo demais recebe a recusa do servidor, que é a verdadeira.
 */
export function refusalFor(
  act: TicketAct,
  d: TicketSummary,
  viewer: TicketViewer | null,
  userId: string | undefined,
): string | false {
  if (!viewer || !userId) return false;
  return refusalToActOnTicket(act, subjectFrom(d), { ...viewer, userId }) ?? false;
}
